import { Box, Skeleton } from '@shared/components';

import { CELL_DIVIDER_SX, GROUP_DIVIDER_SX, METRIC_COLUMNS } from './columns';
import { type QuestionnaireMetric } from './types';
import { formatMetricValue } from './utils';

const CELL_PADDING_X = 1.5;
const cellBaseSx = {
  px: CELL_PADDING_X,
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  ...CELL_DIVIDER_SX,
} as const;

interface MetricGroupCellsProps {
  metric: QuestionnaireMetric;
  fontWeight?: number;
  height: number;
  /**
   * When true, each metric cell renders a pulsing `<Skeleton>` placeholder
   * instead of the formatted numeric value. Non-metric cells are unaffected.
   * @default false
   */
  loading?: boolean;
  /**
   * When false, the last cell in the group drops its right divider so the
   * next group's black left divider is not doubled up with the light-grey
   * cell divider. The final group in the row keeps the divider.
   * @default true
   */
  isLastGroup?: boolean;
}

export default function MetricGroupCells({
  metric,
  fontWeight,
  height,
  loading = false,
  isLastGroup = true,
}: MetricGroupCellsProps) {
  const lastIndex = METRIC_COLUMNS.length - 1;

  return (
    <>
      {METRIC_COLUMNS.map((col, index) => (
        <Box
          key={col.key}
          role='cell'
          sx={{
            ...cellBaseSx,
            width: col.width,
            height,
            justifyContent: 'flex-end',
            fontWeight,
            ...(index === 0 ? GROUP_DIVIDER_SX : {}),
            ...(index === lastIndex && !isLastGroup ? { borderRight: 'none' } : {}),
          }}
        >
          {loading ? (
            <Skeleton variant='text' animation='pulse' sx={{ width: '100%' }} />
          ) : (
            formatMetricValue(col.key, metric)
          )}
        </Box>
      ))}
    </>
  );
}
